/** Privacy-reviewed, consented acquisition metadata. Never contains SDK/user IDs. */
export interface AcquisitionTouch {
  captured: boolean;
  surface: "web" | "native";
  provenance: "consented_document" | "consented_activity_unavailable" | "native_metadata" | "native_unavailable" | "historical_evidence";
  landing_path: string | null;
  referrer: string | null;
  market: string | null;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_term: string | null;
  utm_content: string | null;
  gclid: string | null;
  fbclid: string | null;
}
export const ACQUISITION_SNAPSHOT_KEY = "_bac_acquisition_v1";
const markets = new Set(["us", "uk", "dk", "de", "fr", "es", "it", "nl", "se", "no", "fi", "au", "ca", "nz", "ie", "ch", "at", "be", "pt", "pl", "za"]);
const utms = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"] as const;
function text(raw: unknown, max: number): string | null {
  return typeof raw === "string" && raw.length <= max && !/[\u0000-\u001f\u007f@]/.test(raw) ? raw.trim() || null : null;
}
export function normalizeAcquisition(raw: unknown, marketing: boolean): AcquisitionTouch | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  const native = r.surface === "native";
  if (!native && r.documentEntry === false) {
    return { captured: false, surface: "web", provenance: "consented_activity_unavailable",
      landing_path: null, referrer: null, market: null,
      utm_source: null, utm_medium: null, utm_campaign: null, utm_term: null, utm_content: null,
      gclid: null, fbclid: null };
  }
  let url: URL | null = null;
  if (typeof r.url === "string" && r.url.length <= 2048) {
    try { url = new URL(r.url); } catch { return null; }
    if (!["https:", "http:"].includes(url.protocol) || url.username || url.password) return null;
  }
  if (!url && !native) return null;
  // Auth callbacks, private pages and same-origin navigation are not acquisition.
  if (url && /^\/(?:[a-z]{2}\/)?(?:auth|checkout|login|signup|messages|account|dashboard)(?:\/|$)/i.test(url.pathname)) return null;
  let referrer: string | null = null;
  if (typeof r.referrer === "string" && r.referrer.length > 2048) return null;
  if (typeof r.referrer === "string" && r.referrer) {
    try {
      const ref = new URL(r.referrer);
      if (!["https:", "http:"].includes(ref.protocol)) return null;
      if (url && ref.origin === url.origin) return null;
      referrer = ref.origin.length <= 255 ? ref.origin : null;
    } catch { return null; }
  }
  const data: AcquisitionTouch = {
    captured: !!url, surface: native ? "native" : "web",
    provenance: native ? url ? "native_metadata" : "native_unavailable" : "consented_document",
    landing_path: url ? text(url.pathname, 512) : null, referrer,
    market: null, utm_source: null, utm_medium: null, utm_campaign: null,
    utm_term: null, utm_content: null, gclid: null, fbclid: null,
  };
  const prefix = url?.pathname.split("/")[1]?.toLowerCase();
  if (prefix && markets.has(prefix)) data.market = prefix;
  const params = new URLSearchParams();
  for (const key of utms) {
    data[key] = text(url?.searchParams.get(key), 160);
    if (data[key]) params.set(key, data[key]!);
  }
  if (data.landing_path && params.size) data.landing_path += `?${params}`;
  for (const key of ["gclid", "fbclid"] as const) {
    const value = url?.searchParams.get(key);
    data[key] = marketing && value && /^[A-Za-z0-9._:-]{1,200}$/.test(value) ? value : null;
  }
  return data;
}
const searchHosts = ["google.com", "google.co.uk", "google.de", "google.fr", "google.dk", "google.es", "google.it", "google.ca", "google.com.au", "google.co.nz", "google.ie", "google.co.za", "google.nl", "google.se", "google.no", "google.fi", "google.be", "google.at", "google.ch", "google.pt", "google.pl", "google.com.br", "google.com.mx", "bing.com", "duckduckgo.com", "yahoo.com", "yahoo.co.uk", "yahoo.co.jp", "yandex.com", "yandex.ru", "baidu.com"];
const aiHosts = ["chatgpt.com", "chat.openai.com", "perplexity.ai", "claude.ai", "gemini.google.com", "copilot.microsoft.com"];
export function acquisitionChannel(touch: Partial<AcquisitionTouch> | null | undefined): string {
  if (!touch?.captured) return "Unknown";
  if (["cpc", "ppc", "paid"].includes(touch.utm_medium?.trim().toLowerCase() || "")) return "Paid";
  if (!touch.referrer) return "Direct";
  let host: string;
  try { host = new URL(touch.referrer).hostname.toLowerCase().replace(/\.$/, ""); } catch { return "Unknown"; }
  const matches = (h: string) => host === h || host.endsWith(`.${h}`);
  // A Google-owned AI endpoint is not a Google search endpoint.
  if (!matches("gemini.google.com") && searchHosts.some(matches)) return "Organic Search";
  if (aiHosts.some(matches)) return "AI";
  return "Referral";
}
